import { FormEvent, useEffect, useState } from "react";
import {
  Building2,
  ChevronDown,
  MapPin,
  Pencil,
  Plus,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  useBusiness,
  useToggleBranchesVisibility,
} from "@/hooks/useBusinesses";
import {
  type BranchPayload,
  useBranchInventory,
  useBranches,
  useCreateBranch,
  useUpdateBranch,
  useUploadBranchPhotos,
  useSaveBranchInventory,
  useSetBranchStatus,
} from "@/hooks/useBranches";

interface Props {
  businessId: string;
}

const blankBranch = (): BranchPayload => ({
  address: "",
  city: "",
  state: "Kerala",
  isVisibleInCustomerApp: true,
});

export const BranchesTab = ({ businessId }: Props) => {
  const { data: business } = useBusiness(businessId);
  const toggleBranchesVisibility = useToggleBranchesVisibility();
  const { data: branches, isLoading } = useBranches(businessId);
  const create = useCreateBranch(businessId);
  const update = useUpdateBranch(businessId);
  const uploadPhotos = useUploadBranchPhotos(businessId);
  const setStatus = useSetBranchStatus(businessId);
  const [adding, setAdding] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState<string>();
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [draft, setDraft] = useState<BranchPayload>(blankBranch);
  const [reusedPhotoUrls, setReusedPhotoUrls] = useState<string[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>();
  const { data: rooms, isLoading: inventoryLoading } = useBranchInventory(
    businessId,
    selectedBranchId,
  );
  const saveInventory = useSaveBranchInventory(businessId, selectedBranchId);
  const [inventory, setInventory] = useState<
    Array<{ roomTypeId: string; totalRooms: number; isBookable: boolean }>
  >([]);

  useEffect(() => {
    if (!selectedBranchId && branches?.[0]) setSelectedBranchId(branches[0].id);
  }, [branches, selectedBranchId]);

  useEffect(() => {
    if (rooms) {
      setInventory(
        rooms.map((room) => {
          const existing = room.branchInventories[0];
          return {
            roomTypeId: room.id,
            totalRooms: existing?.totalRooms ?? 0,
            isBookable: existing?.isBookable ?? false,
          };
        }),
      );
    }
  }, [rooms]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.address.trim() || !draft.city.trim())
      return toast.error("Address and city are required");
    try {
      const payload: BranchPayload = {
        ...draft,
        name: draft.name?.trim() || undefined,
        address: draft.address.trim(),
        city: draft.city.trim(),
        phone: draft.phone?.trim() || undefined,
        email: draft.email?.trim() || undefined,
        whatsapp: draft.whatsapp?.trim() || undefined,
        lat:
          draft.lat === undefined || draft.lat === null
            ? undefined
            : Number(draft.lat),
        lng:
          draft.lng === undefined || draft.lng === null
            ? undefined
            : Number(draft.lng),
        isVisibleInCustomerApp: draft.isVisibleInCustomerApp ?? true,
        photoUrls: reusedPhotoUrls,
      };
      const branch = editingBranchId
        ? await update.mutateAsync({ branchId: editingBranchId, data: payload })
        : await create.mutateAsync(payload);
      if (photoFiles.length && branch.id)
        await uploadPhotos.mutateAsync({
          branchId: branch.id,
          files: photoFiles,
        });
      toast.success(
        editingBranchId
          ? "Branch details updated"
          : "Branch created. Configure its room capacity before booking.",
      );
      setDraft(blankBranch());
      setAdding(false);
      setEditingBranchId(undefined);
      setPhotoFiles([]);
      setReusedPhotoUrls([]);
      setSelectedBranchId(branch.id);
    } catch (error: any) {
      toast.error(error?.message ?? "Could not save branch");
    }
  };

  const startEdit = (branch: NonNullable<typeof branches>[number]) => {
    setEditingBranchId(branch.id);
    setAdding(false);
    setDraft({
      name: branch.name ?? "",
      address: branch.address,
      city: branch.city,
      district: branch.district ?? "",
      state: branch.state ?? "",
      pincode: branch.pincode ?? "",
      phone: branch.phone ?? "",
      email: branch.email ?? "",
      whatsapp: branch.whatsapp ?? "",
      lat: branch.lat ?? undefined,
      lng: branch.lng ?? undefined,
      isVisibleInCustomerApp: branch.isVisibleInCustomerApp ?? true,
    });
    const existingUrls = branch.photos?.map((p) => p.url) ?? [];
    if (branch.isPrimary && existingUrls.length === 0) {
      const businessCover = business?.photos?.find((p) => p.isCover)?.url;
      setReusedPhotoUrls(businessCover ? [businessCover] : []);
    } else {
      setReusedPhotoUrls(existingUrls);
    }
  };

  const updateInventory = (
    roomTypeId: string,
    patch: Partial<{ totalRooms: number; isBookable: boolean }>,
  ) =>
    setInventory((current) =>
      current.map((item) =>
        item.roomTypeId === roomTypeId ? { ...item, ...patch } : item,
      ),
    );

  if (isLoading)
    return (
      <div className="space-y-3">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );

  return (
    <div className="space-y-5">
      {/* Master Branch Visibility Control */}
      <Card className="border-2 border-primary/20 bg-muted/20">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="space-y-1 max-w-xl">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-base">
                  Show Branches in Customer App
                </h4>
                <Badge
                  variant={
                    business?.showBranchesInCustomerApp !== false
                      ? "default"
                      : "secondary"
                  }
                >
                  {business?.showBranchesInCustomerApp !== false
                    ? "🟢 Active & Visible"
                    : "⚪ Hidden from Customers"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Master control switch for branch locations. When enabled, branch
                setup is active and branches are visible to customers in the
                Customer App. When disabled, branch locations and the Branches
                tab are hidden on the profile screen, while keeping the main
                centre visible to customers.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {business?.showBranchesInCustomerApp !== false
                  ? "🟢 Visible to Customers"
                  : "⚪ Hidden from Customers"}
              </span>
              <Switch
                checked={business?.showBranchesInCustomerApp !== false}
                disabled={toggleBranchesVisibility.isPending}
                onCheckedChange={(checked) => {
                  toggleBranchesVisibility.mutate(
                    { id: businessId, showBranchesInCustomerApp: checked },
                    {
                      onSuccess: () =>
                        toast.success(
                          `Branches ${checked ? "enabled" : "hidden"} in Customer App`,
                        ),
                      onError: (err: any) =>
                        toast.error(
                          err?.message ??
                            "Could not update branches visibility",
                        ),
                    },
                  );
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {business?.showBranchesInCustomerApp === false && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs text-amber-800 dark:text-amber-300">
          ℹ️ Branch setup is currently turned OFF for customers. Branch
          locations and the Branches tab are hidden on the Centre profile in the
          Customer App, while the main centre remains visible in search and
          listings.
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-semibold">Branches</h3>
          <p className="text-sm text-muted-foreground">
            Locations share this business’s rooms, pricing, services, staff, and
            policies. Capacity is configured separately per branch.
          </p>
        </div>
        <Button
          onClick={() => {
            setAdding((value) => !value);
            setEditingBranchId(undefined);
            setDraft(blankBranch());
            const isPrimaryBranch = !branches?.length;
            if (isPrimaryBranch) {
              const businessCover = business?.photos?.find(
                (p) => p.isCover,
              )?.url;
              setReusedPhotoUrls(businessCover ? [businessCover] : []);
            } else {
              setReusedPhotoUrls([]);
            }
          }}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add branch
        </Button>
      </div>

      {(adding || editingBranchId) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editingBranchId ? "Edit branch" : "New branch"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="branch-name">Branch name</Label>
                <Input
                  id="branch-name"
                  value={draft.name ?? ""}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Kakkanad Centre"
                />
              </div>
              <div>
                <Label htmlFor="branch-phone">Phone</Label>
                <Input
                  id="branch-phone"
                  value={draft.phone ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, phone: e.target.value })
                  }
                  placeholder="+91…"
                />
              </div>
              <div>
                <Label htmlFor="branch-whatsapp">WhatsApp</Label>
                <Input
                  id="branch-whatsapp"
                  value={draft.whatsapp ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, whatsapp: e.target.value })
                  }
                  placeholder="+91…"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="branch-address">Address *</Label>
                <Input
                  id="branch-address"
                  value={draft.address}
                  onChange={(e) =>
                    setDraft({ ...draft, address: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="branch-city">City *</Label>
                <Input
                  id="branch-city"
                  value={draft.city}
                  onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="branch-district">District</Label>
                <Input
                  id="branch-district"
                  value={draft.district ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, district: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="branch-state">State</Label>
                <Input
                  id="branch-state"
                  value={draft.state ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, state: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="branch-pincode">Pincode</Label>
                <Input
                  id="branch-pincode"
                  value={draft.pincode ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, pincode: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="branch-email">Email</Label>
                <Input
                  id="branch-email"
                  type="email"
                  value={draft.email ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, email: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="branch-lat">Latitude</Label>
                <Input
                  id="branch-lat"
                  type="number"
                  step="any"
                  min={-90}
                  max={90}
                  value={draft.lat ?? ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      lat:
                        e.target.value === ""
                          ? undefined
                          : Number(e.target.value),
                    })
                  }
                  placeholder="9.9312"
                />
              </div>
              <div>
                <Label htmlFor="branch-lng">Longitude</Label>
                <Input
                  id="branch-lng"
                  type="number"
                  step="any"
                  min={-180}
                  max={180}
                  value={draft.lng ?? ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      lng:
                        e.target.value === ""
                          ? undefined
                          : Number(e.target.value),
                    })
                  }
                  placeholder="76.2673"
                />
              </div>

              <div className="md:col-span-2 rounded-lg border p-4 bg-muted/30 space-y-2">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="branch-customer-app-toggle"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Show in Customer App
                  </Label>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">
                      {draft.isVisibleInCustomerApp !== false
                        ? "🟢 Visible to Customers"
                        : "⚪ Hidden from Customers"}
                    </span>
                    <Switch
                      id="branch-customer-app-toggle"
                      checked={draft.isVisibleInCustomerApp !== false}
                      onCheckedChange={(checked) =>
                        setDraft({ ...draft, isVisibleInCustomerApp: checked })
                      }
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  When enabled, this branch will be visible to customers in the
                  Customer App. When disabled, the branch will be hidden from
                  all customer listings and search results, but will remain
                  accessible in the Admin Panel.
                </p>
              </div>

              <div className="md:col-span-2 space-y-3">
                <Label>Branch photos</Label>

                {business?.photos && business.photos.length > 0 && (
                  <div className="space-y-2 border rounded-lg p-3 bg-muted/20">
                    <span className="text-xs font-medium text-muted-foreground block mb-1">
                      Select photos to reuse from the main business gallery:
                    </span>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {business.photos.map((photo) => {
                        const isSelected = reusedPhotoUrls.includes(photo.url);
                        return (
                          <button
                            key={photo.id}
                            type="button"
                            onClick={() => {
                              setReusedPhotoUrls((prev) =>
                                prev.includes(photo.url)
                                  ? prev.filter((u) => u !== photo.url)
                                  : [...prev, photo.url],
                              );
                            }}
                            className={`relative aspect-video rounded-md overflow-hidden border-2 transition ${
                              isSelected
                                ? "border-primary ring-2 ring-primary/20"
                                : "border-transparent opacity-60 hover:opacity-100"
                            }`}
                          >
                            <img
                              src={photo.url}
                              className="w-full h-full object-cover"
                              alt=""
                            />
                            {photo.isCover && (
                              <span className="absolute top-1 left-1 text-[8px] bg-amber-500 text-white font-bold px-1 rounded">
                                Cover
                              </span>
                            )}
                            {isSelected && (
                              <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                                <span className="text-xs bg-primary text-primary-foreground font-bold px-1.5 py-0.5 rounded-full">
                                  ✓
                                </span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground block">
                    Or upload new photos from device:
                  </span>
                  <Input
                    id="branch-photos"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) =>
                      setPhotoFiles(Array.from(e.target.files ?? []))
                    }
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Up to 8 photos total.
                  </p>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAdding(false);
                    setEditingBranchId(undefined);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    create.isPending ||
                    update.isPending ||
                    uploadPhotos.isPending
                  }
                >
                  {create.isPending ||
                  update.isPending ||
                  uploadPhotos.isPending
                    ? "Saving…"
                    : editingBranchId
                      ? "Save changes"
                      : "Create branch"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {!branches?.length ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <Building2 className="mx-auto mb-3 h-7 w-7" />
            No branches yet. Add the first location for this business.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {branches.map((branch) => (
            <details
              key={branch.id}
              open={selectedBranchId === branch.id}
              onToggle={(event) => {
                if ((event.currentTarget as HTMLDetailsElement).open)
                  setSelectedBranchId(branch.id);
              }}
              className={`rounded-lg border bg-card ${selectedBranchId === branch.id ? "border-primary" : "border-border"}`}
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 [&::-webkit-details-marker]:hidden">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">
                      {branch.name || "Unnamed branch"}
                    </span>
                    {branch.isPrimary && (
                      <Badge variant="secondary">Primary</Badge>
                    )}
                    <Badge
                      variant={
                        branch.status === "ACTIVE" ? "default" : "outline"
                      }
                    >
                      {branch.status === "ACTIVE" ? "Active" : "Inactive"}
                    </Badge>
                    <Badge
                      variant={
                        branch.isVisibleInCustomerApp ? "outline" : "secondary"
                      }
                      className={
                        branch.isVisibleInCustomerApp
                          ? "text-emerald-700 dark:text-emerald-400 border-emerald-300"
                          : "text-muted-foreground"
                      }
                    >
                      {branch.isVisibleInCustomerApp
                        ? "🟢 Visible to Customers"
                        : "⚪ Hidden from Customers"}
                    </Badge>
                  </div>
                  <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {branch.address}, {branch.city}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {
                      branch.roomInventories.filter((item) => item.isBookable)
                        .length
                    }{" "}
                    bookable room types · {branch._count?.bookings ?? 0}{" "}
                    bookings
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Edit branch"
                    onClick={(event) => {
                      event.preventDefault();
                      startEdit(branch);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {branch.status === "ACTIVE" ? "Active" : "Inactive"}
                  </span>
                  <Switch
                    checked={branch.status === "ACTIVE"}
                    disabled={branch.isPrimary || setStatus.isPending}
                    onCheckedChange={(checked) =>
                      setStatus.mutate(
                        {
                          branchId: branch.id,
                          status: checked ? "ACTIVE" : "INACTIVE",
                        },
                        {
                          onSuccess: () =>
                            toast.success(
                              `Branch ${checked ? "activated" : "deactivated"}`,
                            ),
                          onError: (error: any) =>
                            toast.error(
                              error?.message ?? "Could not update branch",
                            ),
                        },
                      )
                    }
                    onClick={(event) => event.stopPropagation()}
                  />
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </div>
              </summary>
              {selectedBranchId === branch.id && (
                <div className="border-t px-4 pb-4 pt-3">
                  <Card className="border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Room capacity</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        A room type is unavailable at this branch until it is
                        marked bookable and has capacity.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {inventoryLoading ? (
                        <Skeleton className="h-24 w-full" />
                      ) : !rooms?.length ? (
                        <p className="text-sm text-muted-foreground">
                          Add shared room types on the Rooms tab first.
                        </p>
                      ) : (
                        <>
                          {rooms.map((room) => {
                            const item = inventory.find(
                              (entry) => entry.roomTypeId === room.id,
                            );
                            return (
                              <div
                                key={room.id}
                                className="grid grid-cols-[1fr_110px_auto] items-center gap-3 rounded-lg border p-3"
                              >
                                <span className="font-medium">{room.name}</span>
                                <Input
                                  aria-label={`${room.name} capacity`}
                                  type="number"
                                  min={0}
                                  value={item?.totalRooms ?? 0}
                                  onChange={(e) =>
                                    updateInventory(room.id, {
                                      totalRooms: Math.max(
                                        0,
                                        Number(e.target.value) || 0,
                                      ),
                                    })
                                  }
                                />
                                <label className="flex items-center gap-2 text-sm">
                                  Bookable
                                  <Switch
                                    checked={item?.isBookable ?? false}
                                    onCheckedChange={(isBookable) =>
                                      updateInventory(room.id, { isBookable })
                                    }
                                  />
                                </label>
                              </div>
                            );
                          })}
                          <div className="flex justify-end">
                            <Button
                              onClick={() =>
                                saveInventory.mutate(inventory, {
                                  onSuccess: () =>
                                    toast.success("Branch capacity saved"),
                                  onError: (error: any) =>
                                    toast.error(
                                      error?.message ??
                                        "Could not save capacity",
                                    ),
                                })
                              }
                              disabled={saveInventory.isPending}
                            >
                              <Save className="mr-1.5 h-4 w-4" />
                              {saveInventory.isPending
                                ? "Saving…"
                                : "Save capacity"}
                            </Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </details>
          ))}
        </div>
      )}
    </div>
  );
};
